import React from 'react';
import classNames from 'classnames';
import { compact, map, SuiteId } from '@libs/shared';
import { Hyperlink } from '@fx-ui/jdy-design-biz';
import { getBrandInfo, getCurrentSuiteId, getSuiteView } from '~/utils/brand';

export interface BrandLogoProps {
    className?: string;
    style?: React.CSSProperties;
    /**
     * 尺寸（固定高度）
     *  - 大logo - 48px
     *  - 中logo - 32px
     *  - 小logo - 24px
     *
     *  @default 'small'
     */
    size?: 'small' | 'middle' | 'large';
    /**
     * logo 是否可点击
     * @default false
     */
    clickable?: boolean;
    /**
     * 自定义平台 logo 地址
     * @default jdy_config.suite_views.0.icon_with_text_uri
     */
    platformLogoUrl?: string;
    /**
     * 自定义平台 logo 跳转链接
     * @default jdy_config.suite_views.0.home_uri
     */
    platformLink?: string;
    /**
     * 是否展示当前 suite logo
     * @default false
     */
    showSuiteLogo?: boolean;
    /**
     * 自定义 suite logo 地址
     * @default jdy_config.suite_views.<currentSuiteId>.icon_with_text_uri
     */
    suiteLogoUrl?: string;
    /**
     * 自定义 suite logo 跳转链接
     * @default jdy_config.suite_views.<currentSuiteId>.home_uri
     */
    suiteLink?: string;
    /**
     * 指定显示某个suite的logo
     */
    suiteId?: SuiteId;
}

interface LogoItem {
    key: 'platform' | 'suite';
    src: string;
    clickable: boolean;
    link?: string;
}

/**
 * 获取平台 logo 或 平台+子产品组合logo
 * 通过 fx-code 渲染时，platformLogo 使用 jdyViews 兜底
 */
const BrandLogo: React.FC<BrandLogoProps> = (props) => {
    const {
        className,
        style,
        size = 'small',
        clickable = false,
        platformLogoUrl,
        platformLink,
        showSuiteLogo = false,
        suiteLogoUrl,
        suiteLink,
        suiteId
    } = props;

    const platformView = getSuiteView(SuiteId.PLATFORM);
    const currentSuiteId = suiteId ?? getCurrentSuiteId();
    const suiteView =
        currentSuiteId === SuiteId.PLATFORM ? undefined : getSuiteView(currentSuiteId);

    const platformLogo = getBrandInfo({ logoWithText: platformLogoUrl }).logoWithText;
    const suiteLogo = suiteLogoUrl || suiteView?.icon_with_text_uri;

    const logoItems: LogoItem[] = compact([
        platformLogo && {
            key: 'platform',
            src: platformLogo,
            clickable,
            link: platformLink || platformView?.home_uri
        },
        showSuiteLogo &&
            suiteLogo &&
            platformLogo !== suiteLogo && {
                key: 'suite',
                src: suiteLogo,
                clickable,
                link: suiteLink || suiteView?.home_uri
            }
    ]);

    if (logoItems.length === 0) {
        return null;
    }

    const renderLogoItem = (item: LogoItem) => {
        const hasLink = item.clickable && item.link;
        const itemClassName = classNames('logo-wrapper', {
            'has-link': hasLink
        });
        const image = <img src={item.src} alt="" className="logo-img" />;

        if (hasLink) {
            return (
                <Hyperlink
                    className={itemClassName}
                    target="_self"
                    compact
                    underline={false}
                    href={item.link}
                >
                    {image}
                </Hyperlink>
            );
        }

        return <span className={itemClassName}>{image}</span>;
    };

    return (
        <div
            className={classNames('fx-brand-logo', className, {
                'large-logo': size === 'large',
                'small-logo': size === 'small',
                'middle-logo': size === 'middle',
                'is-combined': logoItems.length > 1
            })}
            style={style}
        >
            {map(logoItems, (item, index) => (
                <React.Fragment key={item.key}>
                    {index > 0 && <div className="logo-split" />}
                    {renderLogoItem(item)}
                </React.Fragment>
            ))}
        </div>
    );
};

export default BrandLogo;
